import React, { useState } from "react";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import {
  Box,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Switch,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTheme } from "@mui/styles";
import EditIcon from "../../globalStyles/icons/editIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import { formatDate } from "../../utils/formatDate";
import FilterModal from "../filterModal";
import TableHeader from "../table-header";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    padding: "6px",
    textAlign: "start",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "4px",
    textAlign: "start",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
  },
  "&:lastChild td, &:lastChild th": {
    border: 0,
  },
}));

const SidemenuTableCell = ({ x, handleEditClick, handleDeleteClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleActionClick = (action) => {
    handlePopoverClose();
    switch (action) {
      case "edit":
        handleEditClick(x);
        break;
      case "delete":
        handleDeleteClick(x);
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <IconButton
        aria-label="more actions"
        onClick={handlePopoverOpen}
        sx={{
          color: open ? "primary.main" : "inherit",
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={1}>
          <Button
            startIcon={<EditIcon />}
            onClick={() => handleActionClick("edit")}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Edit
          </Button>
          <Button
            disabled={x.system_generated}
            startIcon={<DeleteIcon />}
            onClick={() => handleActionClick("delete")}
            fullWidth
            sx={{ justifyContent: "flex-start" }}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const SidemenuList = ({
  data,
  onEditSidemenu,
  onDeleteSidemenu,
  viewMode,
  selectedSidemenu,
  setSelectedSidemenu,
  currentPage,
  itemsPerPage,
  loading,
  onChangeFilter,
  filterData,
}) => {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [sidemenus, setSidemenus] = useState([]);
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;

  const startIndex = currentPage * itemsPerPage;
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    name: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
    type: "",
  });

  const handleDeleteClick = (x) => {
    setSelectedSidemenu(x);
    setOpenConfirmation(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    setLoadingAction(true);
    try {
      await onDeleteSidemenu(selectedSidemenu._id);
      setOpenConfirmation(false);
      setSelectedSidemenu(null);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEditClick = (x) => {
    setSelectedSidemenu(x);
    onEditSidemenu(x);
  };

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData?.columns.find((x) => x?.key == filterKey));
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey: searchKey,
    });
    handleCloseFilter();
  };

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "title",
      title: "Title",
      handleOpenFilter: handleOpenFilter,
      displayKey: "title",
    },
    {
      key: "order",
      title: "Order",
      handleOpenFilter: handleOpenFilter,
      displayKey: "order",
    },

    {
      key: "pkey",
      title: "PKey",
      handleOpenFilter: handleOpenFilter,
      displayKey: "pkey",
    },
    {
      key: "parent",
      title: "Parent",
      handleOpenFilter: handleOpenFilter,
      render: (item) => item.parent || "-",
    },
    {
      key: "system_generated",
      title: "System Generated",
      handleOpenFilter: handleOpenFilter,
      render: (item) => (
        <Chip
          label={item.system_generated ? "Yes" : "No"}
          color={item.system_generated ? "info" : "default"}
          size="small"
        />
      ),
    },
    {
      key: "icon",
      title: "Icon",
      displayKey: "icon",
    },
    {
      key: "active",
      title: "Active",
      handleOpenFilter: handleOpenFilter,
      render: (item) => (
        <Chip
          label={item.active ? "Active" : "Inactive"}
          color={item.active ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      key: "createdAt",
      title: "Created At",
      handleOpenFilter: handleOpenFilter,
      render: (item) => formatDate(item.createdAt),
    },
    {
      key: "action",
      title: "",
      render: (item) => (
        <SidemenuTableCell
          x={item}
          handleEditClick={handleEditClick}
          handleDeleteClick={handleDeleteClick}
        />
      ),
    },
  ];

  const renderListView = () => (
    <TableContainer
      sx={{
        maxHeight: "calc(100vh - 300px)",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#c1c1c1",
          borderRadius: "3px",
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#c1c1c1 #f1f1f1",
      }}
    >
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <StyledTableRow>
            {TableSchema.map((x) => (
              <TableHeader
                filterData={filterData}
                filterKey={x.key}
                handleOpenFilter={x.handleOpenFilter}
                title={x.title}
              />
            ))}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {data?.map((row, index) => (
            <StyledTableRow key={row._id}>
              {TableSchema.map((x) => (
                <StyledTableCell>
                  {x.render ? (
                    x.render(row, index)
                  ) : (
                    <Typography
                      fontSize="12px"
                      color="var(--text-secondary)"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {row[x.displayKey]}
                    </Typography>
                  )}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderGridView = () => (
    <Grid container spacing={2}>
      {data?.map((item, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
          <Card
            sx={{
              cursor: "pointer",
              "&:hover": {
                boxShadow: 3,
              },
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" component="div">
                  {item.title}
                </Typography>
                <Chip
                  label={item.active ? "Active" : "Inactive"}
                  color={item.active ? "success" : "default"}
                  size="small"
                />
              </Box>
              <Typography color="text.secondary" gutterBottom>
                Icon: {item.icon}
              </Typography>
              <Typography color="text.secondary">
                Order: {item.order}
              </Typography>
              <Typography color="text.secondary">PKey: {item.pkey}</Typography>
              <Box mt={2} display="flex" justifyContent="space-between">
                <Button
                  size="small"
                  onClick={() => handleEditClick(item)}
                  startIcon={<EditIcon />}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(item)}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <div>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <div
          style={{
            overflowX: "auto",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": { width: 0 },
          }}
        >
          {viewMode === "grid" ? renderGridView() : renderListView()}
        </div>
      )}

      <DeleteConfirmationModal
        title={`Are you sure you want to delete this Sidemenu item?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={confirmDelete}
      />
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
          type={selectedfilterData?.type}
        />
      </Popover>
    </div>
  );
};

export default SidemenuList;
